import { sortBy, uniq } from 'lodash';

export enum LineType {
  UNCHANGED,
  ADDED,
  REMOVED,
}

export type DiffViewType = 'original' | 'modified';

export type RangeExpandType = 'top' | 'bottom' | 'full';

export type LineNumberPropsFunction = ({
  lineNumber,
  type,
  isChanged,
  totalLineCount,
}: {
  lineNumber: number | undefined;
  type: DiffViewType;
  isChanged: boolean;
  totalLineCount: number;
}) => React.HTMLProps<HTMLDivElement>;

export type TextDiff = {
  originalDiffLines: string[];
  originalDiffLineNumbers: (number | undefined)[];
  modifiedDiffLines: string[];
  modifiedDiffLineNumbers: (number | undefined)[];
  added: number[];
  removed: number[];
};

export type InlineTextDiff = {
  diffLines: string[];
  originalDiffLineNumbers: (number | undefined)[];
  modifiedDiffLineNumbers: (number | undefined)[];
  lineTypes: LineType[];
};

const LINES_BETWEEN_HIDDEN_INDICATORS = 5;

// This is a version of the getRangesToShow from diffHelpers rewritten for
// use with our custom build Prism diffView.
export const getRangesToShow = ({
  linesToShow,
  totalLineCount,
}: {
  linesToShow: number[];
  totalLineCount: number;
}) => {
  const ranges = sortBy(uniq(linesToShow)).reduce(
    (acc: [number, number][], line) => {
      const lastGroup = acc[acc.length - 1];

      if (!lastGroup || lastGroup[lastGroup.length - 1] !== line - 1) {
        acc.push([line, line]);
      }

      acc[acc.length - 1] = [acc?.[acc.length - 1]?.[0] ?? line, line];

      return acc;
    },
    [],
  );

  return ranges.flatMap((range) => {
    const startLineNumber = range[0];
    const endLineNumber = range[1];

    // include 5 lines above and below the change group
    return {
      startLineNumber: Math.max(
        1,
        startLineNumber - LINES_BETWEEN_HIDDEN_INDICATORS,
      ),
      endLineNumber: Math.min(
        totalLineCount,
        endLineNumber + LINES_BETWEEN_HIDDEN_INDICATORS,
      ),
    };
  });
};

/**
 * Given a set S of overlapping or non overlapping ranges in a document,
 * get the ranges of everything not included in S. This is equivalent to
 * invertRangesInDocumentForDiffs which is used for our monaco diff editors.
 */
export const invertRangesInDiffView = ({
  ranges,
  totalLineCount,
}: {
  ranges: ReturnType<typeof getRangesToShow>;
  totalLineCount: number;
}) => {
  return sortBy(ranges, 'startLineNumber').reduce(
    (acc: typeof ranges, range, i) => {
      if (i === 0 && range.startLineNumber !== 1) {
        acc.push({
          startLineNumber: 1,
          endLineNumber: range.startLineNumber - 1,
        });
      }
      if (i === ranges.length - 1 && range.endLineNumber !== totalLineCount) {
        acc.push({
          startLineNumber: range.endLineNumber + 1,
          endLineNumber: totalLineCount,
        });
      } else {
        const nextRangeStart = ranges[i + 1]?.startLineNumber;
        if (nextRangeStart && nextRangeStart > range.endLineNumber)
          acc.push({
            startLineNumber: range.endLineNumber + 1,
            endLineNumber: nextRangeStart - 1,
          });
      }
      return acc;
    },
    [],
  );
};
